<script setup lang="ts">
import { format, parseISO } from 'date-fns';
import { computed, ref } from 'vue';

import { STREAM_BASE_URI } from '@/js/constants';
import { useGlobalStyle, useTemplate } from '@/store/templateData';
import IconMdiCalendarPlus from '~icons/mdi/calendar-plus';

import ICON_LIVE from '../../assets/icons/live.svg';
import ICON_POST_ARROW from '../../assets/icons/post-arrow.svg';
import ICON_POST_PLAY from '../../assets/icons/post-play.svg';
import CalendarDialog from '../CalendarDialog/CalendarDialog.vue';

defineOptions({
  name: 'ModuleUpcomingLiveDisplay'
});

const POST_NUMBER_PER_ROW = 4;

interface Props {
  module?: {
    components?: {
      title?: {
        text?: string;
      };
      subtitle?: {
        text?: string;
      };
    };
  };
}

const props = withDefaults(defineProps<Props>(), {
  module: () => ({
    components: {
      title: {
        text: ''
      },
      subtitle: {
        text: ''
      }
    }
  })
});

const components = computed(() => props.module?.components);

const { upcomingPostList, handsupConfig } = useTemplate();
const slug = computed(() => handsupConfig.value?.store?.slug);

const normalizedPosts = computed(() => {
  return upcomingPostList.value.map((post: any) => {
    return {
      ...post,
      imageUrl: post.image?.[0]?.url ?? '',
      upcomingAt: post.upcoming_at
        ? format(parseISO(post.upcoming_at), 'MM/dd HH:mm')
        : '',
      titleDisplay: post.upcoming_title || post.title,
      upcomingLiveUrl: post.share_url ?? `${STREAM_BASE_URI}/${slug.value}/live/${post.id}`,
    };
  });
});

const needRender = computed(() => [
  normalizedPosts.value.length,
].some(Boolean));

const postStyle = computed(() => {
  const isPostMoreThanOneRow = normalizedPosts.value.length > POST_NUMBER_PER_ROW;

  return {
    '--post-col': isPostMoreThanOneRow ? POST_NUMBER_PER_ROW : normalizedPosts.value.length,
  };
});

const selectedPost = ref<{ titleDisplay?: string; upcomingLiveUrl?: string; upcoming_at?: string } | null>(null);
const isCalendarOpen = ref(false);
const openCalendar = (post: any) => {
  selectedPost.value = post;
  isCalendarOpen.value = true;
};

const closeCalendar = () => {
  isCalendarOpen.value = false;
  selectedPost.value = null;
};

const { titleStyle, subtitleStyle } = useGlobalStyle();
</script>

<template>
<div class="module-upcoming-live-display" v-if="needRender">
  <div
    class="module-upcoming-live-display__title"
    v-if="components?.title?.text"
    :style="titleStyle"
  >
    {{ components.title.text }}
  </div>

  <div
    class="module-upcoming-live-display__subtitle"
    v-if="components?.subtitle?.text"
    :style="subtitleStyle"
  >
    {{ components.subtitle.text }}
  </div>

  <div class="module-upcoming-live-display__list" :style="postStyle">
    <a
      class="module-upcoming-live-display__list__item"
      v-for="post in normalizedPosts"
      :key="post.id"
      :href="post.upcomingLiveUrl"
    >
      <div class="module-upcoming-live-display__list__image-wrapper">
        <div class="module-upcoming-live-display__list__image-box module-upcoming-live-display__list__mask">
          <template v-if="post.imageUrl">
            <div class="module-upcoming-live-display__list__image">
              <el-image class="w-full h-full" :src="post.imageUrl" fit="cover"/>
            </div>
            <img class="module-upcoming-live-display__list__play" :src="ICON_POST_PLAY"/>
          </template>
          <template v-else>
            <div class="module-upcoming-live-display__list__placeholder">
              <el-image
                class="module-upcoming-live-display__list__live"
                :src="ICON_LIVE"
              />
              <div class="module-upcoming-live-display__list__description">
                {{ $t("cms_setting.display.upcoming_live_placeholder_desc") }}
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="module-upcoming-live-display__list__content">
        <button
          class="module-upcoming-live-display__calendar"
          type="button"
          :class="{ 'module-upcoming-live-display__calendar--hidden': !post.upcomingAt }"
          @click.prevent="openCalendar(post)"
        >
          <span class="module-upcoming-live-display__calendar__date">
            {{ post.upcomingAt }}
          </span>

          <IconMdiCalendarPlus class="module-upcoming-live-display__calendar__icon"/>
        </button>

        <div class="module-upcoming-live-display__name">
          {{ post.titleDisplay }}
        </div>
      </div>

      <div class="module-upcoming-live-display__list__arrow">
        <img
          class="module-upcoming-live-display__list__arrow__icon"
          :src="ICON_POST_ARROW"
        />
      </div>
    </a>
  </div>

  <CalendarDialog
    v-if="isCalendarOpen"
    :title="selectedPost?.titleDisplay ?? ''"
    :shareUrl="selectedPost?.upcomingLiveUrl ?? ''"
    :startDate="selectedPost?.upcoming_at ?? ''"
    @close="closeCalendar"
  />
</div>
</template>
<style lang="scss" scoped>
$desktop-fixed-width: 1075px;
$cover-mobile-width: 76px;
$calendar-mobile-width: 132px;

.module-upcoming-live-display {
  @apply px-5 py-4;

  &__title,
  &__subtitle {
    @apply font-medium break-all whitespace-pre-line;

    word-wrap: break-word;
  }

  &__title {
    @apply text-2xl text-center mb-4;
  }

  &__subtitle {
    @apply text-xl text-left mb-4;
  }

  &__list {
    @apply grid gap-x-4 grid-cols-1;

    &__item {
      @apply flex flex-row items-center;
      @apply w-full relative;

      color: inherit;
      -webkit-tap-highlight-color: transparent;

      // NOTE: padding 是為了幫上下的線撐出空間，再用 abosolute 來排版，不然會被 Flex 排成一行
      padding-top: 9px;
      padding-bottom: 8px;

      &:hover {
        @apply no-underline;

        color: inherit;
      }

      &::before {
        @apply h-px bg-gray-500;
        @apply absolute left-0 right-0 top-0;

        content: '';
      }

      &:last-child {
        padding-bottom: 10px;

        &::after {
          @apply h-px bg-gray-500;
          @apply absolute left-0 right-0 bottom-0;

          content: '';
        }
      }
    }

    &__mask {
      @apply transition-all duration-300;

      .module-upcoming-live-display__list__play {
        @apply transition-all duration-300;
      }

      @include rwd-hover {
        @apply opacity-50;

        .module-upcoming-live-display__list__play {
          @apply opacity-0;
        }
      }
    }

    &__image-wrapper {
      flex: 0 0 $cover-mobile-width;
    }

    &__image-box {
      @apply relative flex justify-center items-center bg-gray-600;
      @apply rounded-lg;

      @include aspect-ratio($height: 16, $width: 9);
    }

    &__placeholder {
      @apply flex flex-col items-center justify-center;
      @apply bg-cover;

      background-image: url('@/assets/bg-default-cover.svg');
    }

    &__image,
    &__placeholder {
      @apply absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform;
      @apply w-full h-full;
    }

    &__play {
      @apply absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform;
      @apply w-12 h-12;
    }

    &__live {
      @apply w-7 h-7;
    }

    &__description {
      @apply hidden;
      @apply text-xs text-center text-gray-900;
      @apply px-4 mt-2;
    }

    &__content {
      @apply mx-4;

      flex: 1 1 100%;
    }

    &__arrow {
      @apply ml-auto mr-2;

      &__icon {
        @apply h-3.5 w-3;
      }
    }
  }

  &__calendar {
    @apply flex items-center justify-center mb-2;
    @apply border border-black border-solid rounded-full outline-none;
    @apply transition ease-linear;

    color: var(--custom_product_button_color, black);
    background-color: var(--custom_product_button_bg_color, white);
    opacity: var(--custom_product_button_opacity, 1);
    border-color: var(--custom_product_button_border_color, black);
    fill: var(--custom_product_button_color, black);
    padding-top: 3px;
    padding-bottom: 3px;
    padding-left: 18px;
    padding-right: 16px;

    &__date {
      @apply text-sm font-bold;
    }

    &__icon {
      @apply ml-1.5 text-sm leading-none;
    }

    &--hidden {
      @apply hidden;
    }
  }

  .module-upcoming-live-display__calendar:enabled {
    @include rwd-hover {
      color: var(--custom_product_button_color_hover, white);
      background-color: var(--custom_product_button_bg_color_hover, black);
      opacity: var(--custom_product_button_opacity_hover, 1);
      fill: var(--custom_product_button_color_hover, white);
    }
  }

  &__name {
    @apply text-sm font-normal mb-0 line-clamp-3;
  }
}

// 平板
.tablet .module-upcoming-live-display {
  @apply px-10;

  &__title {
    @apply text-4xl mb-6;
  }

  &__subtitle {
    @apply text-2xl mb-4;
  }

  &__list {
    @apply grid gap-x-4 grid-cols-2;

    &__image-wrapper {
      flex: 1 1 auto;
    }

    &__item {
      @apply flex-col mb-6 p-0;

      align-items: normal;

      &::before,
      &::after {
        @apply hidden;
      }
    }

    &__image-box {
      @apply mb-3 rounded-2xl;
    }

    &__live {
      @apply w-10 h-10;
    }

    &__description {
      @apply block;
    }

    &__content {
      @apply mx-0;

      flex: 1 1 auto;
      max-width: none;
    }

    &__arrow {
      @apply hidden;
    }
  }

  &__calendar {
    @apply w-full mb-3;

    &--hidden {
      @apply block;

      visibility: hidden;
    }
  }

  &__name {
    @apply mb-1;
  }
}

// PC - 1024px
.laptop .module-upcoming-live-display {
  @apply py-8;

  padding-left: 10%;
  padding-right: 10%;

  &__title {
    @apply text-4xl mb-6;
  }

  &__subtitle {
    @apply text-2xl mb-4;
  }

  &__list {
    @apply grid gap-x-8 justify-center;

    // NOTE: 96px 是由上方 gap-x-8 乘上 3 個 gap 所得到
    grid-template-columns: repeat(var(--post-col), minmax(0, calc((100% - 96px) / 4)));

    &__item {
      @apply flex-col mb-6 p-0;

      align-items: normal;

      &::before,
      &::after {
        @apply hidden;
      }
    }

    &__image-box {
      @apply mb-3 rounded-2xl;
    }

    &__live {
      @apply w-10 h-10;
    }

    &__description {
      @apply block;
    }

    &__content {
      @apply mx-0;

      flex: 1 1 auto;
      max-width: none;
    }

    &__arrow {
      @apply hidden;
    }
  }

  &__name {
    @apply mb-1;
  }

  &__calendar {
    @apply w-full mb-3;

    &--hidden {
      @apply block;

      visibility: hidden;
    }
  }
}

// PC - 1440px
.desktop .module-upcoming-live-display {
  @apply py-8;

  width: $desktop-fixed-width;

  &__title {
    @apply text-4xl mb-6;
  }

  &__subtitle {
    @apply text-2xl mb-4;
  }

  &__list {
    @apply grid gap-x-8 justify-center;

    // NOTE: 96px 是由上方 gap-x-8 乘上 3 個 gap 所得到
    grid-template-columns: repeat(var(--post-col), minmax(0, calc((100% - 96px) / 4)));

    &__item {
      @apply flex-col mb-6 p-0;

      align-items: normal;

      &::before,
      &::after {
        @apply hidden;
      }
    }

    &__image-box {
      @apply mb-3 rounded-2xl;
    }

    &__live {
      @apply w-10 h-10;
    }

    &__description {
      @apply block;
    }

    &__content {
      @apply mx-0;

      flex: 1 1 auto;
      max-width: none;
    }

    &__arrow {
      @apply hidden;
    }
  }

  &__name {
    @apply mb-1;
  }

  &__calendar {
    @apply w-full mb-3;

    &--hidden {
      @apply block;

      visibility: hidden;
    }
  }
}
</style>
